import { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  Stack,
  Tooltip,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
// Imports from project
import "../../Style/dialogstyle.scss";
import EmptyPDF from "../../Assets/pdf.png";
import EmptyImage from "../../Assets/empty-image.png";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import useBookContentForm from "./Hooks/useBookContentForm";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import { UploadImageNoCrop } from "../CropImage/UploadImageNoCrop";
import { uploadPdf } from "../CropImage/UploadPdf";
import LoadingProgess from "../../Utils/LoadingProgress";
import FileThumnail from "../../Utils/FileThumnail";

export default function BookContentForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  bookId,
  hideBackdrop = false,
}) {
  const { t } = useContext(AuthContext);
  const [fileType, setFileType] = useState(editData?.fileType || "image");

  // console.log("fileType::", fileType)

  // =================================== Upload Image ====================================
  const mutationDelete = useDeleteImageServer();
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [statusProgress, setStatusProgress] = useState(false);
  const [progress, setProgress] = useState(10);
  // console.log("progress::", progress)

  const handleUploadImage = async (e) => {
    const imageFile = e.target.files[0];
    setStatusProgress(true);
    if (imageFile) {
      setImageFile(imageFile);
      const uploadedImg = await UploadImageNoCrop(imageFile);
      setProfileHook(uploadedImg);
    }
  };

  const handleUploadPDF = async (e) => {
    const pdfFile = e.target.files?.[0];

    if (!pdfFile) return;

    if (pdfFile.type !== "application/pdf") {
      alert("Please select a PDF file");
      e.target.value = "";
      return;
    }

    setStatusProgress(true);

    let pdfUrl;

    try {
      // 1. Upload PDF to server
      const uploadedPdf = await uploadPdf(pdfFile);
      // 2. Store the server URL/path
      setProfileHook(uploadedPdf);
    } catch (error) {
      console.error("PDF upload error:", error);
    } finally {
      // 4. Release temporary URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }

      setStatusProgress(false);
      e.target.value = "";
    }
  };

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  // ================================= End upload Image ===============================

  // ============================== Formik & Validation ===============================
  const CheckValidation = Yup.object().shape({
    pageNumber: Yup.number().required(t("required") || "Required"),
    title: Yup.string().required(t("required") || "Required"),
    description: Yup.string(),
    image: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      pageNumber: 1,
      description: "",
      pages: 0,
      image: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      // console.log("pdfPreview::", pdfFile, pdfPreview);

      if (dialogTitle === "Create") {
        createBookContent({
          variables: {
            input: {
              bookId: bookId,
              title: values.title,
              pageNumber: values.pageNumber,
              description: values.description || "",
              fileType,
              // file: fileType === "image" ? profileHook : pdfPreview,
              file: profileHook,
            },
          },
        });
      } else {
        // console.log("editData?._id::", editData?._id);
        updateBookContent({
          variables: {
            id: editData?._id,
            input: {
              bookId: bookId,
              title: values.title,
              pageNumber: values.pageNumber,
              description: values.description || "",
              fileType,
              // file:
              //   fileType === "image" && profileHook
              //     ? profileHook
              //     : fileType === "pdf" && pdfPreview
              //       ? pdfPreview
              //       : editData?.file,
              file: profileHook ? profileHook : editData?.file,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    handleChange,
  } = formik;

  // console.log("values::", values)

  const { createBookContent, loading, updateBookContent, setLoading } =
    useBookContentForm({
      mutationDelete,
      editData,
      profileHook,
      resetForm,
      setRefetch,
      handleClose,
    });

  // console.log("editData::", editData);

  useEffect(() => {
    if (open) {
      if (dialogTitle === "Update" && editData) {
        setFieldValue("title", editData?.title || "");
        setFieldValue("pageNumber", editData?.pageNumber);
        setFieldValue("description", editData?.description || "");
      } else {
        resetForm();
      }
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_content")
            : t("modal_title_update_content")
        }
        classTitle="dialog-title"
        classIcon="close-icon"
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_page_number")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  name="pageNumber"
                  value={values?.pageNumber || ""}
                  onChange={handleChange}
                  placeholder={t("thead_page_number") || "Page number"}
                  error={Boolean(touched.pageNumber && errors.pageNumber)}
                  helperText={touched.pageNumber && errors.pageNumber}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth size="small" className={"text-field"}>
                  <Select
                    value={fileType}
                    onChange={({ target }) => setFileType(target?.value)}
                  >
                    <MenuItem value={"image"}>Image</MenuItem>
                    <MenuItem value={"pdf"}>PDF</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Image Preview / Upload */}
              <Grid item xs={12}>
                {fileType === "image" && (
                  <Box>
                    <Stack direction="row" justifyContent="center">
                      <Tooltip title="click to upload">
                        <Button component="label">
                          <TextField
                            className="text-field"
                            type="file"
                            id="image"
                            sx={{ display: "none" }}
                            onChange={handleUploadImage}
                          />
                          <img
                            className="book-image"
                            alt=""
                            src={
                              imageFile
                                ? URL.createObjectURL(imageFile)
                                : editData?.file
                                  ? editData?.file
                                  : EmptyImage
                            }
                          />
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}

                {fileType === "pdf" && (
                  <Box>
                    <Stack direction="row" justifyContent="center">
                      <Tooltip title="Click to upload PDF">
                        <Button component="label">
                          <input
                            type="file"
                            accept="application/pdf"
                            hidden
                            onChange={handleUploadPDF}
                          />

                          {profileHook ? (
                            <FileThumnail pdfFile={profileHook} width="120px" />
                          ) : editData?.file ? (
                            <FileThumnail
                              pdfFile={editData?.file}
                              width="120px"
                            />
                          ) : (
                            <img
                              className="book-image"
                              alt="PDF preview"
                              src={EmptyPDF}
                            />
                          )}
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}

                {statusProgress && progress < 100 ? (
                  <LoadingProgess
                    progress={progress}
                    setProgress={setProgress}
                  />
                ) : (
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ textAlign: "center", marginTop: "5px" }}
                  >
                    {fileType === "pdf" ? t("thead_pdf") : t("thead_image")}
                  </Typography>
                )}
              </Grid>

              {/* Title */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_title")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  name="title"
                  value={values?.title || ""}
                  onChange={handleChange}
                  placeholder={t("thead_title") || "Title"}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_description")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  name="description"
                  value={values?.description || ""}
                  onChange={handleChange}
                  placeholder={t("thead_description") || "Description"}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                {loading ? (
                  <Button className="btn-create" fullWidth disabled>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    type="submit"
                    className="btn-create"
                    disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
